import { Card, Spinner } from '@helix-ds/react'
import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useOrderTracker } from '../hooks/useOrderTracker'

function OrderTrackerLoadingCard() {
  const intl = useIntl()
  const loadingLabel = intl.formatMessage({
    id: 'home.orderTracker.loading',
    defaultMessage: 'Loading order status…',
  })

  return (
    <section aria-busy="true" aria-live="polite" aria-label={loadingLabel}>
      <Card>
        <div className="flex min-h-44 flex-col items-center justify-center gap-3">
          <Spinner color="info" size="md" aria-hidden="true" />
          <p className="text-text-body text-sm font-medium dark:text-gray-300">
            <FormattedMessage id="home.orderTracker.loading" defaultMessage="Loading order status…" />
          </p>
        </div>
      </Card>
    </section>
  )
}

function OrderPlacedIcon({ active }: { active: boolean }) {
  return (
    <svg
      viewBox="0 0 8.98356 11.3169"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="h-3.5 w-3.5"
      aria-hidden="true"
    >
      <path
        d="M8.16667 1.57511C8.16667 1.5287 8.14822 1.48432 8.1154 1.4515C8.08258 1.41868 8.03819 1.40023 7.99178 1.40023H6.24178C6.17886 1.40023 6.12013 1.38406 6.06689 1.35864V2.33333H6.24178C6.4673 2.33333 6.65023 2.51626 6.65023 2.74178C6.65023 2.9673 6.4673 3.15023 6.24178 3.15023H2.74178C2.51626 3.15023 2.33333 2.9673 2.33333 2.74178C2.33333 2.51626 2.51626 2.33333 2.74178 2.33333H2.91667V1.35864C2.86344 1.38406 2.8047 1.40023 2.74178 1.40023H0.991781C0.945368 1.40023 0.900983 1.41868 0.868164 1.4515C0.835345 1.48432 0.816895 1.5287 0.816895 1.57511V10.3251C0.816895 10.3715 0.835346 10.4159 0.868164 10.4487C0.900982 10.4815 0.945367 10.5 0.991781 10.5H7.99178C8.0382 10.5 8.08258 10.4815 8.1154 10.4487C8.14822 10.4159 8.16667 10.3715 8.16667 10.3251V1.57511ZM2.74748 7.58333C2.97299 7.58333 3.15592 7.76626 3.15592 7.99178C3.15592 8.2173 2.97299 8.40023 2.74748 8.40023H2.74178C2.51626 8.40023 2.33333 8.2173 2.33333 7.99178C2.33333 7.76626 2.51626 7.58333 2.74178 7.58333H2.74748ZM6.24178 7.58333C6.4673 7.58333 6.65023 7.76626 6.65023 7.99178C6.65023 8.2173 6.4673 8.40023 6.24178 8.40023H4.49178C4.26626 8.40023 4.08333 8.2173 4.08333 7.99178C4.08333 7.76626 4.26626 7.58333 4.49178 7.58333H6.24178ZM2.74748 5.25C2.97299 5.25 3.15592 5.43293 3.15592 5.65845C3.15592 5.88396 2.97299 6.06689 2.74748 6.06689H2.74178C2.51626 6.06689 2.33333 5.88396 2.33333 5.65845C2.33333 5.43293 2.51626 5.25 2.74178 5.25H2.74748ZM6.24178 5.25C6.4673 5.25 6.65023 5.43293 6.65023 5.65845C6.65023 5.88396 6.4673 6.06689 6.24178 6.06689H4.49178C4.26626 6.06689 4.08333 5.88396 4.08333 5.65845C4.08333 5.43293 4.26626 5.25 4.49178 5.25H6.24178ZM3.73356 2.33333H5.25V0.816895H3.73356V2.33333ZM8.98356 10.3251C8.98356 10.5881 8.87901 10.8404 8.69303 11.0264C8.50706 11.2123 8.25479 11.3169 7.99178 11.3169H0.991781C0.728775 11.3169 0.476501 11.2123 0.290527 11.0264C0.104554 10.8404 0 10.5881 0 10.3251V1.57511C0 1.31211 0.104554 1.05983 0.290527 0.873861C0.476501 0.687887 0.728774 0.583333 0.991781 0.583333H2.74178C2.80462 0.583333 2.86349 0.598994 2.91667 0.624349V0.408447C2.91667 0.182931 3.0996 0 3.32511 0H5.65845C5.88396 0 6.06689 0.182931 6.06689 0.408447V0.624349C6.12007 0.598994 6.17895 0.583333 6.24178 0.583333H7.99178C8.25479 0.583333 8.50706 0.687887 8.69303 0.873861C8.87901 1.05983 8.98356 1.31211 8.98356 1.57511V10.3251Z"
        fill={active ? 'white' : '#79716B'}
      />
    </svg>
  )
}

function ProcessingIcon({ active }: { active: boolean }) {
  return (
    <svg
      viewBox="0 0 9.00799 11.3168"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="h-3.5 w-3.5"
      aria-hidden="true"
    >
      <path
        d="M7.05355 0.119557C7.21298 -0.0398797 7.47171 -0.0398248 7.63118 0.119557L8.88843 1.3768C9.04781 1.53627 9.04787 1.795 8.88843 1.95444C8.72898 2.11379 8.47023 2.1138 8.31079 1.95444L7.9707 1.61435L7.29053 2.29395L7.63061 2.63461C7.78999 2.79407 7.78999 3.05224 7.63061 3.21168L7.05241 3.78989C7.54747 4.41936 7.81841 5.19842 7.81689 6.00302L7.81234 6.18588C7.75035 7.42285 7.06158 8.49456 6.05892 9.08375C6.06341 9.108 6.06689 9.13282 6.06689 9.15837V10.4999H8.57511C8.80063 10.4999 8.98356 10.6829 8.98356 10.9084C8.98356 11.1339 8.80063 11.3168 8.57511 11.3168H0.408447C0.182931 11.3168 0 11.1339 0 10.9084C0 10.6829 0.182931 10.4999 0.408447 10.4999H2.91667V9.28712C2.77707 9.22797 2.641 9.15978 2.50936 9.08261C1.98663 8.77615 1.55292 8.3398 1.24813 7.81682H0.408447C0.182931 7.81682 0 7.63389 0 7.40837C0 7.18286 0.182931 6.99993 0.408447 6.99993H3.90845C4.13396 6.99993 4.31689 7.18286 4.31689 7.40837C4.31689 7.63389 4.13396 7.81682 3.90845 7.81682H2.24674C2.4391 8.03855 2.66704 8.22882 2.92236 8.37851C3.33618 8.62106 3.80704 8.74859 4.2867 8.74936C5.78131 8.74922 7 7.52395 7 6.00302V6.00245C7.00127 5.41442 6.81456 4.84344 6.47021 4.37208L5.1167 5.72616C5.04015 5.80272 4.93614 5.84577 4.82788 5.84579C4.71962 5.84579 4.61563 5.80269 4.53906 5.72616L4.18644 5.37354L3.61393 5.94719C3.45447 6.10666 3.19576 6.10666 3.0363 5.94719C2.87683 5.78773 2.87683 5.52902 3.0363 5.36956L3.60937 4.79648L3.28182 4.46892C3.12245 4.30946 3.12241 4.05073 3.28182 3.89129L5.79631 1.3768L5.8584 1.32553C5.92497 1.28118 6.00395 1.25717 6.08512 1.25717C6.19336 1.25724 6.29742 1.30024 6.37394 1.3768L6.71346 1.71689L7.39307 1.03671L7.05355 0.697193C6.89419 0.53775 6.89419 0.279 7.05355 0.119557ZM5.25 9.43124C4.94384 9.51871 4.62096 9.5668 4.2867 9.56682H4.28556C4.09988 9.56653 3.91556 9.54929 3.73356 9.52011V10.4999H5.25V9.43124ZM4.1477 4.17953L4.82788 4.85971L6.76416 2.92286L6.08455 2.24269L4.1477 4.17953Z"
        fill={active ? 'white' : '#79716B'}
      />
    </svg>
  )
}

function ReviewIcon({ active }: { active: boolean }) {
  return (
    <svg
      viewBox="0 0 11.3169 11.3169"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="h-3.5 w-3.5"
      aria-hidden="true"
    >
      <path
        d="M10.5 5.65845C10.5 4.37436 9.9901 3.14277 9.08211 2.23478C8.17412 1.32679 6.94254 0.816895 5.65845 0.816895C4.37436 0.816895 3.14277 1.32679 2.23478 2.23478C1.32679 3.14277 0.816895 4.37436 0.816895 5.65845C0.816895 6.29426 0.942149 6.92414 1.18547 7.51156C1.42878 8.09886 1.78527 8.6326 2.23478 9.08211C2.6843 9.53163 3.21804 9.88811 3.80534 10.1314C4.39276 10.3747 5.02263 10.5 5.65845 10.5C6.29426 10.5 6.92414 10.3747 7.51156 10.1314C8.09886 9.88811 8.6326 9.53163 9.08211 9.08211C9.53163 8.6326 9.88811 8.09886 10.1314 7.51156C10.3747 6.92414 10.5 6.29426 10.5 5.65845ZM7.11963 4.20296C7.27909 4.0435 7.5378 4.0435 7.69727 4.20296C7.85673 4.36243 7.85673 4.62113 7.69727 4.7806L5.36393 7.11393C5.20447 7.2734 4.94576 7.2734 4.7863 7.11393L3.32796 5.6556C3.1685 5.49613 3.1685 5.23743 3.32796 5.07796C3.48743 4.9185 3.74613 4.9185 3.9056 5.07796L5.07511 6.24748L7.11963 4.20296ZM11.3169 5.65845C11.3169 6.40147 11.1706 7.13726 10.8862 7.82373C10.6019 8.51023 10.1852 9.13432 9.65975 9.65975C9.13432 10.1852 8.51023 10.6019 7.82373 10.8862C7.13726 11.1706 6.40147 11.3169 5.65845 11.3169C4.91543 11.3169 4.17963 11.1706 3.49316 10.8862C2.80666 10.6019 2.18257 10.1852 1.65715 9.65975C1.13172 9.13432 0.715022 8.51023 0.430664 7.82373C0.146338 7.13726 0 6.40147 0 5.65845C3.47694e-08 4.15776 0.596002 2.71829 1.65715 1.65715C2.71829 0.596002 4.15776 0 5.65845 0C7.15913 0 8.59861 0.596002 9.65975 1.65715C10.7209 2.71829 11.3169 4.15776 11.3169 5.65845Z"
        fill={active ? 'white' : '#79716B'}
      />
    </svg>
  )
}

type StepNumber = 1 | 2 | 3

const STEPS: Array<{
  stepNumber: StepNumber
  labelId: string
  labelDefault: string
  bodyId: string
  bodyDefault: string
  Icon: React.FC<{ active: boolean }>
}> = [
  {
    stepNumber: 1,
    labelId: 'home.orderTracker.step1.label',
    labelDefault: 'Order Placed',
    bodyId: 'home.orderTracker.step1.body',
    bodyDefault: "Your child's test has started. We'll let you know when there's something to do next.",
    Icon: OrderPlacedIcon,
  },
  {
    stepNumber: 2,
    labelId: 'home.orderTracker.step2.label',
    labelDefault: 'Processing',
    bodyId: 'home.orderTracker.step2.body',
    bodyDefault:
      "Specialists and your care team are working on the results. We'll let you know as soon as there's something to share.",
    Icon: ProcessingIcon,
  },
  {
    stepNumber: 3,
    labelId: 'home.orderTracker.step3.label',
    labelDefault: 'Review & Next Steps',
    bodyId: 'home.orderTracker.step3.body',
    bodyDefault:
      'Results delivered to your doctor. Your care team is reviewing them now and will be in touch soon to discuss next steps.',
    Icon: ReviewIcon,
  },
]

const TestingProgressCard: React.FC = () => {
  const { step, variant, loading } = useOrderTracker()

  if (loading) {
    return <OrderTrackerLoadingCard />
  }

  if (variant === 'hidden') {
    return null
  }

  const activeStep = step ?? 1
  const activeStepData = STEPS.find((s) => s.stepNumber === activeStep)

  return (
    <Card>
      <div className="flex flex-col gap-1">
        <p className="text-text-body text-sm font-medium tracking-wider uppercase dark:text-gray-400">
          <FormattedMessage id="home.orderTracker.eyebrow" defaultMessage="Order Tracker" />
        </p>
        <h2 className="text-text-heading text-2xl leading-8 font-semibold">
          <FormattedMessage id="home.orderTracker.heading" defaultMessage="Tracking your child's test" />
        </h2>
      </div>

      <div className="relative mt-6">
        <div aria-hidden="true" className="bg-border-base absolute inset-x-0 top-[14px] h-px" />
        <ol aria-label="Testing progress steps" className="flex items-start justify-between">
          {STEPS.map(({ stepNumber, labelId, labelDefault, Icon }) => {
            const isActive = stepNumber === activeStep
            const isCompleted = stepNumber < activeStep

            return (
              <li
                key={stepNumber}
                aria-current={isActive ? 'step' : undefined}
                className="relative z-10 flex flex-1 flex-col items-center gap-1"
              >
                <div
                  className={[
                    'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2',
                    isActive
                      ? 'border-brand-200 bg-brand-700'
                      : isCompleted
                        ? 'border-brand-200 bg-brand-700'
                        : 'border-border-base bg-bg-secondary',
                  ].join(' ')}
                >
                  <Icon active={isActive || isCompleted} />
                </div>
                <span
                  className={[
                    'text-center text-sm',
                    isActive
                      ? 'text-brand-700 font-bold'
                      : isCompleted
                        ? 'text-text-body font-medium'
                        : 'text-text-heading font-medium',
                  ].join(' ')}
                >
                  <FormattedMessage id={labelId} defaultMessage={labelDefault} />
                </span>
              </li>
            )
          })}
        </ol>
      </div>

      {activeStepData && (
        <p className="text-text-body mt-6 text-sm leading-5">
          <FormattedMessage id={activeStepData.bodyId} defaultMessage={activeStepData.bodyDefault} />
        </p>
      )}
    </Card>
  )
}

export default TestingProgressCard
